import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import OfflineSyncBanner from "@/components/OfflineSyncBanner";

import Login from "@/pages/Login";
import Home from "@/pages/Home";
import MachineChecksheet from "@/pages/MachineChecksheet";
import RMReceipt from "@/pages/RMReceipt";
import ProductionStage1 from "@/pages/ProductionStage1";
import ProductionStage2 from "@/pages/ProductionStage2";
import MissingData from "@/pages/MissingData";
import QualityInspection from "@/pages/QualityInspection";
import Dashboard from "@/pages/Dashboard";
import Notifications from "@/pages/Notifications";
import AdminPanel from "@/pages/AdminPanel";
import RMIssueLog from "@/pages/RMIssueLog";
import InspectionResults from "@/pages/InspectionResults";
import ProductionHistory from "@/pages/ProductionHistory";
import PublicTrace from "@/pages/PublicTrace";
import VisualInspection from "@/pages/VisualInspection";
import ReworkQueue from "@/pages/ReworkQueue";
import ClassifyStrokes from "@/pages/ClassifyStrokes";
import SetupStrokeLedger from "@/pages/SetupStrokeLedger";
import DowntimeReport from "@/pages/DowntimeReport";
import ShiftSummary from "@/pages/ShiftSummary";
import MachineDeepDive from "@/pages/MachineDeepDive";
import InspectionQueue from "@/pages/InspectionQueue";
import OperatorPerformance from "@/pages/OperatorPerformance";
import ToolHistory from "@/pages/ToolHistory";
import BulkToolSetup from "@/pages/BulkToolSetup";
import BulkMachineSetup from "@/pages/BulkMachineSetup";
import PmCalendar from "@/pages/PmCalendar";
import SetupChecksheet from "@/pages/SetupChecksheet";
import LoadedTools from "@/pages/LoadedTools";

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1A3560] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/home" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <OfflineSyncBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/public-trace" element={<PublicTrace />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checksheet"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Main Admin", "Supervisor"]}>
                <MachineChecksheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rm-receipt"
            element={
              <ProtectedRoute roles={["Stores", "Main Admin", "Supervisor", "Inspector"]}>
                <RMReceipt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production/start"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Main Admin", "Supervisor"]}>
                <ProductionStage1 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production/update"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Main Admin", "Supervisor"]}>
                <ProductionStage2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production/update/:jobId"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Main Admin", "Supervisor"]}>
                <ProductionStage2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/missing-data"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin"]}>
                <MissingData />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspection"
            element={
              <ProtectedRoute roles={["Inspector", "Main Admin", "Supervisor"]}>
                <QualityInspection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/machine/:machineCode/classify"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Supervisor", "Main Admin"]}>
                <ClassifyStrokes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/setup-ledger"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin"]}>
                <SetupStrokeLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/downtime-report"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin", "Inspector"]}>
                <DowntimeReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shift-summary"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin", "Inspector"]}>
                <ShiftSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/machine-deep-dive/:machineCode"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin", "Inspector"]}>
                <MachineDeepDive />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspection-queue"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <InspectionQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operator-performance"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <OperatorPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tool-history"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <ToolHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tool-history/:toolNo"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <ToolHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-tool-setup"
            element={
              <ProtectedRoute roles={["Main Admin"]}>
                <BulkToolSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bulk-machine-setup"
            element={
              <ProtectedRoute roles={["Main Admin"]}>
                <BulkMachineSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pm-calendar"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <PmCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/setup-checksheet/new/:jobId"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Supervisor", "Main Admin"]}>
                <SetupChecksheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/setup-checksheet/new"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Supervisor", "Main Admin"]}>
                <SetupChecksheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/setup-checksheet/view/:checksheetId"
            element={
              <ProtectedRoute roles={["Operator", "Inspector", "Supervisor", "Main Admin"]}>
                <SetupChecksheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/loaded-tools"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <LoadedTools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/visual-inspection"
            element={
              <ProtectedRoute>
                <VisualInspection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rework-queue"
            element={
              <ProtectedRoute>
                <ReworkQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["Main Admin", "Supervisor"]}>
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rm-issues"
            element={
              <ProtectedRoute roles={["Stores", "Main Admin", "Supervisor"]}>
                <RMIssueLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspection-results"
            element={
              <ProtectedRoute roles={["Inspector", "Supervisor", "Main Admin"]}>
                <InspectionResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production-history"
            element={
              <ProtectedRoute roles={["Supervisor", "Main Admin"]}>
                <ProductionHistory />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
